import { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Share2, Calculator } from "lucide-react";
import { api, setPageMeta, setJsonLd, SITE_BASE } from "@/lib/api";
import { Reveal } from "@/components/Motion";
import { JoinForm } from "@/components/JoinForm";
import { Breadcrumbs } from "@/components/Breadcrumbs";

// Audit NRC-070: route readers from guides into the matching calculator
const TOOL_MATCH = [
  [/heart-rate|zone/, ["heart-rate-zones", "Heart Rate Zones Calculator"]],
  [/hyrox/, ["hyrox-finish-time", "HYROX Finish Time Predictor"]],
  [/fuel|gel|carb/, ["race-fueling-planner", "Race Fueling Planner"]],
  [/protein/, ["runner-protein-calculator", "Runner Protein Calculator"]],
  [/macro/, ["runner-macro-calculator", "Runner Macro Calculator"]],
  [/tdee|calorie|weight-loss|body-comp/, ["runner-tdee-calculator", "Runner TDEE Calculator"]],
  [/predict|race-time|goal-time|qualify/, ["race-predictor", "Race Time Predictor"]],
  [/mileage|base-build|taper|comeback|return-to-running/, ["mileage-progression", "Mileage Progression Planner"]],
  [/recovery|sleep|overtraining/, ["recovery-readiness-score", "Recovery Readiness Score"]],
  [/longevity/, ["runner-longevity-score", "Runner Longevity Score"]],
  [/strength|lifting|one-rep/, ["one-rep-max-calculator", "One-Rep Max Calculator"]],
  [/nutrition|supplement|creatine|hydration|electrolyte|meal/, ["runner-tdee-calculator", "Runner TDEE Calculator"]],
  [/pace|split|negative|tempo|interval|speed/, ["pace-calculator", "Pace Calculator"]],
  [/marathon|half|5k|10k|ultra|ironman/, ["race-predictor", "Race Time Predictor"]],
];

const SISTER_RE = /ninjathlete\.com|ninjathleticlub\.com/;

const SectionTable = ({ table }) => (
  <div className="overflow-x-auto my-6 border border-white/15">
    <table className="w-full text-sm">
      <thead>
        <tr className="bg-white text-ink">
          {table.headers.map((h) => (
            <th key={h} className="text-left font-mono-label px-4 py-3">{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {table.rows.map((row, i) => (
          <tr key={`${i}-${row[0]}`} className="border-t border-white/10">
            {row.map((cell, j) => (
              <td key={`${j}-${cell}`} className={`px-4 py-3 ${j === 0 ? "text-white/90 font-semibold" : "text-white/60"}`}>{cell}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export default function ArticlePage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setData(null);
    setNotFound(false);
    api.get(`/articles/${slug}`)
      .then((r) => {
        setData(r.data);
        const a = r.data.article;
        const title = a.slug === "running-terms-glossary"
          ? "Running Terms Glossary: 70 Words Explained | Ninja Run Club"
          : `${a.title} | Ninja Run Club`;
        setPageMeta(title, a.excerpt, {
          type: "article",
          published: a.published || a.updated,
          modified: a.updated,
        });
      })
      .catch(() => setNotFound(true));
  }, [slug]);

  useEffect(() => {
    if (!data) return;
    const { article } = data;
    const ld = [
      {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: article.title,
        description: article.excerpt,
        author: { "@type": "Organization", name: article.author || "NRC Editorial Team", url: "https://ninjarunclub.com/about" },
        publisher: {
          "@type": "Organization",
          name: "Ninja Run Club",
          url: "https://ninjarunclub.com",
          logo: { "@type": "ImageObject", url: "https://ninjarunclub.com/brand/nr-mark.png" },
        },
        reviewedBy: { "@type": "Organization", name: article.reviewer || "NRC Coaching Collective" },
        datePublished: article.published || article.updated,
        dateModified: article.updated,
        mainEntityOfPage: `https://ninjarunclub.com/training/${article.slug}`,
      },
    ];
    if ((article.faq || []).length > 0) {
      ld.push({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: article.faq.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      });
    }
    if (article.schema_markup && typeof article.schema_markup === "object") {
      ld.push(article.schema_markup);
    }
    return setJsonLd("article-jsonld", ld);
  }, [data]);

  useEffect(() => {
    if (notFound) setPageMeta("Guide Not Found | Ninja Run Club", "", { noindex: true });
  }, [notFound]);

  // Audit NRC-052: consolidated into the Dallas city hub
  if (slug === "best-run-clubs-in-dallas") {
    return <Navigate to="/run-clubs/texas/dallas" replace />;
  }

  if (notFound) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4 px-5">
        <p className="font-display text-4xl">GUIDE NOT FOUND.</p>
        <Link to="/training" className="font-mono-label link-underline text-white/60">BACK TO TRAINING</Link>
      </div>
    );
  }
  if (!data) return <p className="font-mono-label text-white/40 py-32 text-center">LOADING...</p>;

  const { article, related } = data;
  const matchTarget = `${article.slug} ${article.category}`.toLowerCase();
  const tool = (TOOL_MATCH.find(([re]) => re.test(matchTarget)) || [])[1];
  const sisterSources = (article.sources || []).filter((s) => SISTER_RE.test(s.url));
  const externalSources = (article.sources || []).filter((s) => !SISTER_RE.test(s.url));

  const share = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success("Link copied.");
  };

  return (
    <div className="bg-night min-h-screen">
      <header className="px-5 lg:px-10 pt-10 pb-12 border-b border-white/10 bg-ink">
        <div className="max-w-6xl mx-auto">
        <Breadcrumbs light items={[["Home", "/"], ["Training", "/training"], [article.title, `/training/${article.slug}`]]} />
        <Link to="/training" className="mt-5 inline-flex items-center gap-2 font-mono-label text-white/40 hover:text-white transition-colors duration-200">
          <ArrowLeft size={13} /> TRAINING HUB
        </Link>
        <Reveal>
          <p className="font-mono-label text-white/50 mt-8">{article.category} · {article.read_time} MIN READ</p>
          <h1 data-testid="article-title" className="font-display text-3xl sm:text-4xl lg:text-6xl mt-4 max-w-4xl leading-[0.98]">{article.title}</h1>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-6 font-mono-label text-white/40">
            <span>BY {article.author?.toUpperCase()}</span>
            <span>REVIEWED BY {article.reviewer?.toUpperCase()}</span>
            {article.published && article.published !== article.updated && (
              <span>PUBLISHED <time dateTime={article.published}>{article.published}</time></span>
            )}
            <span>UPDATED <time dateTime={article.updated}>{article.updated}</time></span>
            <button onClick={share} data-testid="article-share-button" className="flex items-center gap-1.5 hover:text-white transition-colors duration-200">
              <Share2 size={12} /> SHARE
            </button>
          </div>
        </Reveal>
        </div>
      </header>

      <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_300px] lg:gap-14 px-5 lg:px-10 max-w-6xl mx-auto">
      <article className="py-12 max-w-3xl min-w-0">
        <div data-testid="article-direct-answer" className="border-l-2 border-white pl-6 py-2 mb-8">
          <p className="font-mono-label text-white/40 mb-3">THE SHORT ANSWER</p>
          <p className="text-white/90 text-base lg:text-lg leading-relaxed">{article.direct_answer}</p>
        </div>

        {tool && (
          <Link
            to={`/tools/${tool[0]}`}
            data-testid="article-tool-cta"
            className="group flex items-center justify-between gap-4 border border-white/25 p-5 mb-12 hover:bg-white hover:text-ink transition-colors duration-300"
          >
            <div className="flex items-center gap-4">
              <Calculator size={20} className="shrink-0 opacity-60" />
              <div>
                <p className="font-mono-label opacity-50">DO THE MATH — FREE TOOL</p>
                <p className="font-display text-lg lg:text-xl mt-1">{tool[1].toUpperCase()} →</p>
              </div>
            </div>
          </Link>
        )}

        {article.sections.map((s, i) => (
          <section key={s.heading} id={`section-${i}`} className="mb-12 scroll-mt-24">
            <h2 className="font-display text-2xl lg:text-4xl mb-5">{s.heading}</h2>
            {s.body.split("\n\n").map((p, j) => (
              <p key={`${j}-${p.slice(0, 30)}`} className="text-white/65 leading-relaxed mb-4 text-sm lg:text-base">{p}</p>
            ))}
            {s.table && <SectionTable table={s.table} />}
          </section>
        ))}

        <section className="border border-white/15 p-8 mb-12" data-testid="article-takeaways">
          <p className="font-mono-label text-white/40 mb-5">KEY TAKEAWAYS</p>
          <ul className="space-y-3">
            {article.takeaways.map((t, i) => (
              <li key={t} className="flex gap-4 text-white/80 text-sm">
                <span className="font-mono-label text-white/30">0{i + 1}</span> {t}
              </li>
            ))}
          </ul>
        </section>

        {(article.faq || []).length > 0 && (
          <section className="mb-12" data-testid="article-faq">
            <h2 className="font-display text-2xl lg:text-3xl mb-4">FAQ</h2>
            {article.faq.map((f, i) => (
              <details key={f.q} className="group border-b border-white/10" data-testid={`article-faq-${i}`}>
                <summary className="text-left text-white/80 text-sm cursor-pointer list-none py-4 flex items-center justify-between gap-4 hover:text-white transition-colors duration-200">
                  {f.q}
                  <span aria-hidden="true" className="text-white/40 group-open:rotate-45 transition-transform duration-200 text-lg leading-none shrink-0">+</span>
                </summary>
                <p className="text-white/60 text-sm pb-5">{f.a}</p>
              </details>
            ))}
          </section>
        )}

        {(article.city_links || []).length > 0 && (
          <div className="flex flex-wrap gap-3 mb-12">
            {article.city_links.map((l) => (
              <Link key={l.path} to={l.path} className="btn-invert font-mono-label border border-white/30 px-4 py-2.5 hover:bg-white hover:text-ink">
                {l.label.toUpperCase()}
              </Link>
            ))}
          </div>
        )}

        {externalSources.length > 0 && (
          <div className="mb-12 text-sm">
            <p className="font-mono-label text-white/40 mb-3">SOURCES</p>
            {externalSources.map((s) => (
              <a key={s.url} href={s.url} target="_blank" rel="noopener noreferrer" className="block text-white/50 link-underline w-fit">
                {s.label}
              </a>
            ))}
          </div>
        )}

        {sisterSources.length > 0 && (
          <div className="mb-12 border border-white/15 p-6" data-testid="article-sister-sites">
            <p className="font-mono-label text-white/40 mb-3">FROM OUR SISTER SITE</p>
            {sisterSources.map((s) => (
              <a
                key={s.url}
                href={s.url}
                target="_blank"
                rel={/\/collections\//.test(s.url) ? "sponsored noopener" : "noopener"}
                className="block text-white/60 link-underline w-fit text-sm mb-2 last:mb-0"
              >
                {s.label.replace(/^NinjAthlete:\s*/i, "")} — ninjathlete.com →
              </a>
            ))}
          </div>
        )}

        {article.category === "Gear" && (
          <div className="mb-12 border border-white/15 p-6" data-testid="article-gear-module">
            <p className="font-mono-label text-white/40 mb-3">RUN CLUB GEAR</p>
            <p className="text-white/60 text-sm leading-relaxed">
              Tested opinions deserve tested gear. Browse NRC{" "}
              <Link to="/shop/running-shirts" className="underline underline-offset-4 hover:text-white">running shirts</Link>,{" "}
              <Link to="/shop/running-socks" className="underline underline-offset-4 hover:text-white">running crew socks</Link> and{" "}
              <Link to="/shop/running-hats" className="underline underline-offset-4 hover:text-white">five-panel running hats</Link>{" "}
              — or the full <Link to="/shop" className="underline underline-offset-4 hover:text-white">running apparel shop</Link>.
            </p>
          </div>
        )}
      </article>

      <aside data-testid="article-sidebar" className="hidden lg:block py-12">
        <div className="sticky top-24 space-y-10">
          {article.sections.length > 1 && (
            <div>
              <p className="font-mono-label text-white/40 mb-4">IN THIS GUIDE</p>
              <nav className="border-l border-white/15 space-y-3">
                {article.sections.map((s, i) => (
                  <a
                    key={s.heading}
                    href={`#section-${i}`}
                    data-testid={`article-toc-${i}`}
                    onClick={(e) => {
                      e.preventDefault();
                      document.getElementById(`section-${i}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
                    }}
                    className="block pl-4 text-sm text-white/50 hover:text-white hover:pl-5 transition-all duration-200"
                  >
                    {s.heading}
                  </a>
                ))}
              </nav>
            </div>
          )}
          {related.length > 0 && (
            <div>
              <p className="font-mono-label text-white/40 mb-4">KEEP TRAINING</p>
              <div className="space-y-4">
                {related.slice(0, 4).map((a) => (
                  <Link key={a.slug} to={`/training/${a.slug}`} className="group block">
                    <p className="font-mono-label text-white/30">{a.category}</p>
                    <p className="font-display text-lg leading-tight mt-1 text-white/80 group-hover:text-white transition-colors duration-200">{a.title}</p>
                  </Link>
                ))}
              </div>
            </div>
          )}
          <Link to="/plan" data-testid="article-sidebar-plan-cta" className="btn-invert block border border-white/25 p-5 hover:bg-white hover:text-ink transition-colors duration-200">
            <p className="font-mono-label opacity-50">FREE TOOL</p>
            <p className="font-display text-xl mt-2 leading-tight">BUILD YOUR CUSTOM TRAINING PLAN →</p>
          </Link>
        </div>
      </aside>
      </div>

      {related.length > 0 && (
        <section className="px-5 lg:px-10 py-14 border-t border-white/10" data-testid="article-related">
          <div className="max-w-6xl mx-auto">
          <p className="font-mono-label text-white/40 mb-8">KEEP TRAINING</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10 border border-white/10">
            {related.map((a) => (
              <Link key={a.slug} to={`/training/${a.slug}`} className="group bg-night p-8 hover:bg-white hover:text-ink transition-colors duration-300">
                <p className="font-mono-label opacity-50">{a.category}</p>
                <h3 className="font-display text-xl lg:text-2xl mt-3">{a.title}</h3>
              </Link>
            ))}
          </div>
          </div>
        </section>
      )}

      <section className="light-section bg-paper text-ink px-5 lg:px-10 py-20">
        <div className="max-w-6xl mx-auto">
        <div className="max-w-2xl">
          <h2 className="font-display text-3xl lg:text-4xl">TRAINING DROPS, WEEKLY.</h2>
          <p className="text-ink/60 text-sm mt-3 mb-8">One useful email a week. Plans, tools, and the next run near you.</p>
          <JoinForm compact source={`article-${slug}`} />
        </div>
        </div>
      </section>
    </div>
  );
}
